import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Building2, Wallet, Globe, Shield, TrendingUp, Users, 
  ArrowRight, ChevronRight, Crown, CircleDollarSign,
  Zap, Menu, X, Briefcase, Coins
} from 'lucide-react';

const LandingPage = () => {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const features = [
    {
      icon: <Wallet className="h-8 w-8" />,
      title: "Digital Currency (CBDC)",
      description: "Secure digital wallets with instant transfers, QR payments, and programmable transactions.",
      link: "/citizen"
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Instant Payments",
      description: "Real-time national payment infrastructure with sub-second settlement times.",
      link: "/public"
    },
    {
      icon: <Building2 className="h-8 w-8" />,
      title: "Banking Supervision",
      description: "Comprehensive regulatory portal for licensing, compliance, and risk monitoring.",
      link: "/institution"
    },
    {
      icon: <Coins className="h-8 w-8" />,
      title: "TANUB (₭₸)",
      description: "Gold-pegged sovereign currency. Buy, sell, and convert TNB instantly with real-time rates.",
      link: "/tnb/market"
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Cross-Border Settlement",
      description: "Multi-currency settlement with international banking partners worldwide.",
      link: "/international"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Reserve Transparency",
      description: "Public dashboards for reserves, monetary supply, blockchain ledger, and peg status.",
      link: "/tnb/transparency"
    }
  ];

  const stats = [
    { value: "2.5M+", label: "Active Wallets" },
    { value: "$892B", label: "Assets Managed" },
    { value: "2.3s", label: "Avg Settlement" },
    { value: "99.99%", label: "System Uptime" }
  ];

  const portals = [
    {
      title: "Citizens",
      description: "Access your CBDC wallet, make instant payments, and manage your digital finances.",
      icon: <Users className="h-6 w-6" />,
      link: "/citizen",
      color: "bg-black/5 text-black"
    },
    {
      title: "Financial Institutions",
      description: "Apply for licenses, submit regulatory reports, and monitor liquidity.",
      icon: <Building2 className="h-6 w-6" />,
      link: "/institution",
      color: "bg-black/5 text-black"
    },
    {
      title: "Government",
      description: "Manage treasury operations, sovereign bonds, and wealth fund portfolios.",
      icon: <Crown className="h-6 w-6" />,
      link: "/government",
      color: "bg-gold/10 text-gold"
    },
    {
      title: "International Partners",
      description: "Access cross-border settlement tools and foreign exchange services.",
      icon: <Globe className="h-6 w-6" />,
      link: "/international",
      color: "bg-black/5 text-black"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <div className="w-10 h-10 gold-gradient rounded-sm flex items-center justify-center">
                <Crown className="h-6 w-6 text-black" />
              </div>
              <div className="hidden sm:block">
                <span className="font-serif text-xl font-bold text-black">Royal CBDC</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/tnb/market" className="text-sm font-bold text-black hover:text-gold transition-colors" data-testid="nav-tnb-market">
                TANUB
              </Link>
              <Link to="/public" className="text-sm font-bold text-black hover:text-gold transition-colors" data-testid="nav-public">
                Public Portal
              </Link>
              <Link to="/bonds" className="text-sm font-bold text-black hover:text-gold transition-colors" data-testid="nav-bonds">
                Bond Market
              </Link>
              <Link to="/wealth-fund" className="text-sm font-bold text-black hover:text-gold transition-colors" data-testid="nav-swf">
                Wealth Fund
              </Link>
            </div>

            {/* Right side: Transparency + Auth */}
            <div className="hidden md:flex items-center gap-5">
              <Link to="/tnb/transparency" className="text-sm font-bold text-black hover:text-gold transition-colors" data-testid="nav-transparency">
                Transparency
              </Link>
              {user ? (
                <Link to={user.role === 'citizen' ? '/citizen' : `/${user.role}`}>
                  <Button className="bg-black hover:bg-black/90 text-white font-bold" data-testid="nav-dashboard-btn">Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-black font-bold" data-testid="nav-login-btn">Sign In</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="btn-royal-gold font-bold" data-testid="nav-register-btn">Get Started</Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-black/10 bg-white">
            <div className="px-6 py-4 space-y-3">
              <Link to="/tnb/market" className="block py-2 font-bold text-black" data-testid="mobile-nav-kal">TANUB</Link>
              <Link to="/public" className="block py-2 font-bold text-black" data-testid="mobile-nav-public">Public Portal</Link>
              <Link to="/bonds" className="block py-2 font-bold text-black" data-testid="mobile-nav-bonds">Bond Market</Link>
              <Link to="/wealth-fund" className="block py-2 font-bold text-black" data-testid="mobile-nav-swf">Wealth Fund</Link>
              <Link to="/tnb/transparency" className="block py-2 font-bold text-black" data-testid="mobile-nav-transparency">Transparency</Link>
              {user ? (
                <Link to={user.role === 'citizen' ? '/citizen' : `/${user.role}`}>
                  <Button className="w-full mt-4 bg-black text-white font-bold" data-testid="mobile-nav-dashboard">Dashboard</Button>
                </Link>
              ) : (
                <div className="flex gap-3 mt-4">
                  <Link to="/login" className="flex-1">
                    <Button variant="outline" className="w-full border-black/20 font-bold" data-testid="mobile-nav-login">Sign In</Button>
                  </Link>
                  <Link to="/register" className="flex-1">
                    <Button className="w-full btn-royal-gold font-bold" data-testid="mobile-nav-register">Register</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden bg-black text-white">
        {/* Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Hero Content */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
                <Crown className="h-4 w-4 text-gold" />
                <span className="font-mono text-xs uppercase tracking-widest text-gold">Royal Central Bank Digital Currency</span>
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light tracking-tight leading-[0.95] mb-8">
                The Future of<br />
                <span className="font-semibold text-gold">Sovereign Finance</span>
              </h1>
              <p className="text-lg md:text-xl font-light leading-relaxed text-white/70 max-w-2xl mb-10">
                A secure, scalable CBDC platform for central bank operations, instant payments, 
                banking supervision, and AI-powered financial monitoring — powered by TANUB (₭₸), 
                our gold-pegged national digital currency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto px-8 py-6 text-sm uppercase tracking-widest btn-royal-gold" data-testid="hero-cta-register">
                    Open Digital Wallet
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/tnb/market">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto px-8 py-6 text-sm uppercase tracking-widest border-white/30 text-white hover:bg-white/10" data-testid="hero-cta-trade">
                    Trade TANUB
                  </Button>
                </Link>
              </div>
            </div>

            {/* Stats Card */}
            <div className="lg:col-span-5">
              <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <CircleDollarSign className="h-6 w-6 text-gold" />
                    <span className="font-mono text-xs uppercase tracking-widest text-white/60">Live Statistics</span>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    {stats.map((stat, idx) => (
                      <div key={idx} className="text-center p-4 rounded-lg bg-white/5">
                        <p className="font-mono text-2xl md:text-3xl font-semibold text-gold" data-testid={`stat-${idx}`}>
                          {stat.value}
                        </p>
                        <p className="text-xs text-white/60 mt-1 uppercase tracking-wider">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">
              Digital Infrastructure
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-black tracking-tight">
              Comprehensive Financial Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Link to={feature.link} key={idx} data-testid={`feature-card-${idx}`}>
                <Card className="sovereign-card h-full group cursor-pointer border-black/10 hover:border-gold/30">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-lg bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                      <div className="text-gold">{feature.icon}</div>
                    </div>
                    <h3 className="font-sans text-xl font-semibold text-black mb-3">{feature.title}</h3>
                    <p className="text-black/60 leading-relaxed">{feature.description}</p>
                    <div className="mt-6 flex items-center text-sm font-medium text-gold group-hover:text-gold/80 transition-colors">
                      Learn more <ChevronRight className="ml-1 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Wealth Fund Highlight */}
      <section className="py-24 md:py-32 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">
                National Investment Authority
              </p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light mb-6">
                Sovereign Wealth Fund
              </h2>
              <p className="text-white/70 text-lg mb-8">
                Managing $892.5 billion in national assets across global markets. Our fund delivers 
                consistent returns while maintaining the highest standards of governance and transparency.
              </p>
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div>
                  <p className="font-mono text-3xl font-bold text-gold">9.8%</p>
                  <p className="text-sm text-white/60">YTD Return</p>
                </div>
                <div>
                  <p className="font-mono text-3xl font-bold text-white">7.2%</p>
                  <p className="text-sm text-white/60">Since Inception</p>
                </div>
                <div>
                  <p className="font-mono text-3xl font-bold text-white">45+</p>
                  <p className="text-sm text-white/60">Countries</p>
                </div>
              </div>
              <Link to="/wealth-fund">
                <Button className="btn-royal-gold" data-testid="swf-cta">
                  Explore Fund Details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Global Equities', value: '35%', amount: '$312.4B' },
                { label: 'Fixed Income', value: '30%', amount: '$267.8B' },
                { label: 'Real Estate', value: '15%', amount: '$133.9B' },
                { label: 'Infrastructure', value: '20%', amount: '$178.5B' }
              ].map((item, idx) => (
                <div key={idx} className="p-6 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-white/60 text-sm mb-2">{item.label}</p>
                  <p className="font-mono text-2xl font-bold text-gold">{item.value}</p>
                  <p className="text-white/40 text-sm">{item.amount}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portals Section */}
      <section id="portals" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">
              Access Points
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-black tracking-tight">
              Dedicated Service Portals
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {portals.map((portal, idx) => (
              <Link to={portal.link} key={idx} data-testid={`portal-card-${idx}`}>
                <Card className="sovereign-card h-full group cursor-pointer border-black/10 hover:border-gold/30">
                  <CardContent className="p-8 flex gap-6">
                    <div className={`w-14 h-14 rounded-lg flex items-center justify-center shrink-0 ${portal.color}`}>
                      {portal.icon}
                    </div>
                    <div>
                      <h3 className="font-sans text-xl font-semibold text-black mb-2">{portal.title}</h3>
                      <p className="text-black/60 leading-relaxed">{portal.description}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 gold-gradient">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <Crown className="h-12 w-12 text-black mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-black mb-6">
            Ready to Trade TANUB?
          </h2>
          <p className="text-lg text-black/70 mb-10 max-w-2xl mx-auto">
            Join millions already using TNB for instant, secure, gold-backed digital payments across 25 nations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tnb/market">
              <Button size="lg" className="px-8 py-6 text-sm uppercase tracking-widest bg-black hover:bg-black/90 text-white" data-testid="cta-trade-kal">
                Trade TANUB
              </Button>
            </Link>
            <Link to="/tnb/transparency">
              <Button size="lg" variant="outline" className="px-8 py-6 text-sm uppercase tracking-widest border-black/30 text-black hover:bg-black/5" data-testid="cta-view-reserves">
                View Reserves
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 gold-gradient rounded-sm flex items-center justify-center">
                  <Crown className="h-6 w-6 text-black" />
                </div>
                <span className="font-serif text-xl font-semibold text-white">Royal CBDC</span>
              </div>
              <p className="text-sm text-white/50 leading-relaxed mb-4">
                Royal Central Bank Digital Currency<br />
                Official Sovereign Financial Infrastructure
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-gold/10 border border-gold/20">
                <Shield className="h-4 w-4 text-gold" />
                <span className="text-xs font-mono uppercase tracking-wider text-gold">Royal Authority</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Services</h4>
              <ul className="space-y-2 text-sm text-white/50">
                <li><Link to="/tnb/market" className="hover:text-white transition-colors">TNB Market</Link></li>
                <li><Link to="/citizen" className="hover:text-white transition-colors">Digital Wallet</Link></li>
                <li><Link to="/bonds" className="hover:text-white transition-colors">Bond Market</Link></li>
                <li><Link to="/wealth-fund" className="hover:text-white transition-colors">Wealth Fund</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Portals</h4>
              <ul className="space-y-2 text-sm text-white/50">
                <li><Link to="/citizen" className="hover:text-white transition-colors">Citizens</Link></li>
                <li><Link to="/institution" className="hover:text-white transition-colors">Institutions</Link></li>
                <li><Link to="/government" className="hover:text-white transition-colors">Government</Link></li>
                <li><Link to="/tnb/transparency" className="hover:text-white transition-colors">Transparency</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Resources</h4>
              <ul className="space-y-2 text-sm text-white/50">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Reference</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Support</a></li>
              </ul>
            </div>
          </div>

          <div className="royal-divider mb-8" />

          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/40">
              © 2024 Royal CBDC. All rights reserved.
            </p>
            <div className="flex gap-6 text-xs text-white/40">
              <a href="#" className="hover:text-white/60 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white/60 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white/60 transition-colors">Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
